import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function ScrollingAlphabet() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const [centerIndex, setCenterIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsSticky(scrollPosition > 96); // 24 (pt-24) in pixels
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    if (!containerRef.current || isPaused) return;
    
    let animationFrameId: number;
    const scrollSpeed = 1; // Pixels per frame
    
    const animate = () => {
      const container = containerRef.current;
      if (!container) return;
      
      // Smooth scrolling
      container.scrollLeft += scrollSpeed;
      
      // Calculate center letter index
      const containerWidth = container.offsetWidth;
      const letterWidth = 56; // w-12 (48px) + mx-1 (4px * 2)
      const centerPosition = container.scrollLeft + containerWidth / 2;
      const newCenterIndex = Math.floor(centerPosition / letterWidth) % 26;
      
      if (newCenterIndex !== centerIndex) {
        setCenterIndex(newCenterIndex);
      }
      
      // Reset scroll position when reaching the end of the second set
      if (container.scrollLeft >= (container.scrollWidth * 2) / 3) {
        container.scrollLeft = container.scrollWidth / 3;
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPaused, centerIndex]);

  return (
    <div className={`sticky top-0 z-50 transition-transform duration-300 ${
      isSticky ? 'shadow-lg' : ''
    }`}>
      <div className="bg-gradient-to-r from-primary-50/90 via-accent-50/90 to-primary-50/90 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="relative">
            <div
              ref={containerRef}
              className="overflow-x-hidden whitespace-nowrap select-none py-6"
              style={{ scrollBehavior: 'auto' }}
            >
              {[...letters, ...letters, ...letters].map((letter, index) => {
                const letterIndex = index % 26;
                const isCenter = letterIndex === centerIndex;

                return (
                  <span
                    key={`${letter}-${index}`}
                    className={`inline-block w-10 sm:w-12 text-center py-2 mx-1 text-xl sm:text-2xl font-medium 
                      transition-all duration-300 rounded-lg
                      ${isCenter && !isPaused
                        ? 'bg-white/30 text-gray-800 scale-110 shadow-lg' 
                        : 'text-gray-600/75 hover:bg-white/20 hover:text-gray-800'
                      }`}
                  >
                    {letter}
                  </span>
                );
              })}
            </div>
            
            {/* Pause/Play button */}
            <button
              onClick={() => setIsPaused(!isPaused)}
              className={`absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 rounded-full transition-colors ${
                isPaused ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-white/20 hover:bg-white/30'
              }`}
              title={isPaused ? t('scrolling_alphabet.resume') : t('scrolling_alphabet.pause')}
            >
              {isPaused ? (
                <Play className="w-5 h-5" />
              ) : (
                <Pause className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}